import { isPendingCasinoBetPlayable } from "./casinoPendingExpiry";
import { formatAppTime } from "./appTimeFormat";

export const LIVE_CASINO_STATUSES = ["Active"];
export const PAST_CASINO_STATUSES = ["Completed", "Settling", "Paid"];
export const CASINO_CURRENCY = "COIN";
export const MIN_CASINO_BET = 200;
export const QUICK_CASINO_BETS = [200, 500, 1000, 2000, 5000];

export const formatCasinoCoin = (value) => {
  const num = Math.floor(Number(value) || 0);
  return num.toLocaleString("en-US");
};

/** @deprecated use formatCasinoCoin */
export const formatCasinoCash = formatCasinoCoin;

export function getCasinoGameId(game) {
  return game?._id ? String(game._id) : null;
}

export function getCasinoCreatorId(game) {
  const creator = game?.creator;
  if (!creator) return null;
  return typeof creator === "object" ? String(creator._id || creator.id || "") : String(creator);
}

export function isCasinoCreator(game, userId) {
  if (!game || !userId) return false;
  return getCasinoCreatorId(game) === String(userId);
}

export function upsertCasinoGameInList(games, game) {
  const id = getCasinoGameId(game);
  if (!id) return games;
  const index = games.findIndex((g) => getCasinoGameId(g) === id);
  if (index === -1) return [game, ...games];
  const next = [...games];
  const prev = next[index];
  next[index] = {
    ...prev,
    ...game,
    initialAllocatedAmount: game.initialAllocatedAmount ?? prev.initialAllocatedAmount,
  };
  return next;
}

export function mergeCasinoGamesInList(games, incoming = []) {
  return (incoming || []).reduce((acc, game) => upsertCasinoGameInList(acc, game), games || []);
}

export function sortCasinoGamesForRail(games = []) {
  const list = (games || []).slice();
  list.sort((a, b) => {
    const aLive = LIVE_CASINO_STATUSES.includes(a.status) ? 0 : 1;
    const bLive = LIVE_CASINO_STATUSES.includes(b.status) ? 0 : 1;
    if (aLive !== bLive) return aLive - bLive;
    if (a.status === "Completed" && b.status === "Completed") {
      return new Date(b.updatedAt || b.deadline) - new Date(a.updatedAt || a.deadline);
    }
    return new Date(a.deadline) - new Date(b.deadline);
  });
  return list;
}

export function getCasinoBetId(bet) {
  return bet?._id ? String(bet._id) : null;
}

export function getBetGameId(bet) {
  const game = bet?.game;
  if (!game) return null;
  return typeof game === "object" ? String(game._id || game.id || "") : String(game);
}

export function hasPendingBetOnGame(pendingBets, gameId) {
  return !!getPendingBetForGame(pendingBets, gameId);
}

export function getPendingBetGameIds(pendingBets = []) {
  return new Set(
    (pendingBets || [])
      .map((bet) => getBetGameId(bet))
      .filter(Boolean)
      .map((id) => String(id))
  );
}

export function getPendingBetForGame(pendingBets, gameId) {
  if (!gameId) return null;
  const id = String(gameId);
  return (
    (pendingBets || []).find((bet) => bet?.status === "Pending" && getBetGameId(bet) === id) || null
  );
}

export function getPendingBetsForGame(pendingBets, gameId) {
  if (!gameId) return [];
  const id = String(gameId);
  return (pendingBets || []).filter(
    (bet) => bet?.status === "Pending" && getBetGameId(bet) === id
  );
}

export function sortPendingCasinoBets(pendingBets = []) {
  return (pendingBets || [])
    .filter((bet) => bet?.status === "Pending")
    .slice()
    .sort((a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0));
}

/** All of the user's pending roulette bets across every live table. */
export function getAllPendingRouletteBets(pendingBets = []) {
  return sortPendingCasinoBets(pendingBets).filter((bet) => {
    const game = bet?.game;
    if (!game || typeof game !== "object") return true;
    return !game.gameType || game.gameType === "roulette";
  });
}

/** Extract a user-facing message from a casino socket / thunk error. */
export function getCasinoActionErrorMessage(
  error,
  fallback = "Could not complete Bond Spin action."
) {
  if (!error) return fallback;
  if (typeof error === "string") return error;
  if (typeof error === "object") {
    if (typeof error.error === "string" && error.error.trim()) return error.error;
    if (typeof error.message === "string" && error.message.trim() && error.message !== "Rejected") {
      return error.message;
    }
  }
  return fallback;
}

/** Pending roulette bets that are still within the 90s play window. */
export function getPlayablePendingRouletteBets(pendingBets = [], now = Date.now()) {
  return getAllPendingRouletteBets(pendingBets).filter((bet) =>
    isPendingCasinoBetPlayable(bet, now)
  );
}

export function formatCasinoBetTableLabel(bet) {
  const game = bet?.game;
  if (!game || typeof game !== "object") return "Active table";
  const creatorName = game.creator?.name;
  if (creatorName) return `${creatorName}'s table`;
  if (game.deadline) {
    const time = formatAppTime(game.deadline);
    return `Table · ${time}`;
  }
  return "Active table";
}

export function formatCasinoOutcomeLabel(outcome) {
  if (!outcome) return "";
  const type = outcome.outcomeType || outcome;
  if (type === "ExactNumber" && outcome.value != null) return `Exact #${outcome.value}`;
  return String(type)
    .replace(/_/g, " ")
    .replace(/\bsector\b/i, "Sec")
    .replace(/\bgroup\b/i, "Col");
}

export function upsertPendingBetInList(bets, bet) {
  const id = getCasinoBetId(bet);
  if (!id) return bets || [];
  const list = bets || [];
  const index = list.findIndex((b) => getCasinoBetId(b) === id);
  if (index === -1) return [bet, ...list];
  const next = [...list];
  next[index] = { ...next[index], ...bet };
  return next;
}

/** Max stake the bettor can place for an outcome (wallet cap + table pool ÷ multiplier). */
export function getMaxCasinoBetForOutcome(game, userCoinBalance, outcome) {
  const multiplier = Number(outcome?.multiplier);
  if (!Number.isFinite(multiplier) || multiplier < 1) return 0;

  const pool = Math.floor(Number(game?.allocatedAmount) || 0);
  const wallet = Math.floor(Number(userCoinBalance) || 0);
  const maxFromPool = Math.floor(pool / multiplier);

  return Math.max(0, Math.min(wallet, maxFromPool));
}

export function canAffordCasinoMinBet(game, userCoinBalance, outcome) {
  return getMaxCasinoBetForOutcome(game, userCoinBalance, outcome) >= MIN_CASINO_BET;
}

export function getSuggestedCasinoBetAmounts(maxBet, quickAmounts = QUICK_CASINO_BETS) {
  const max = Math.floor(Number(maxBet) || 0);
  if (max < MIN_CASINO_BET) return [];

  const amounts = (quickAmounts || [])
    .map((amount) => Math.floor(Number(amount) || 0))
    .filter((amount) => amount >= MIN_CASINO_BET && amount <= max);

  if (!amounts.includes(max) && max >= MIN_CASINO_BET) {
    amounts.push(max);
  }

  return [...new Set(amounts)].sort((a, b) => a - b);
}

const PNL_MAG_CAP = 50;
const HOUSE_KEEP_RATE = 0.95;

export function getCasinoTablePnl(game) {
  const current = Math.floor(Number(game?.allocatedAmount) || 0);
  const houseTake = Math.floor(Number(game?.winning) || 0);
  let opened = Math.floor(Number(game?.initialAllocatedAmount) || 0);

  if (!opened && current > 0) {
    const estimatedNet = Math.floor(houseTake * HOUSE_KEEP_RATE);
    opened = Math.max(1, current - estimatedNet);
  }

  if (!opened && !current) {
    return {
      available: false,
      opened: 0,
      current: 0,
      delta: 0,
      pct: 0,
      tone: "flat",
      houseTake,
    };
  }

  const delta = current - opened;
  const pct = opened > 0 ? (delta / opened) * 100 : 0;
  const tone = delta > 0 ? "up" : delta < 0 ? "down" : "flat";

  return {
    available: true,
    opened,
    current,
    delta,
    pct,
    tone,
    houseTake,
  };
}

export function formatPnlCoin(value) {
  const num = Math.floor(Number(value) || 0);
  const abs = Math.abs(num);
  let body = String(abs);
  if (abs >= 1000000000) body = `${(abs / 1000000000).toFixed(1)}B`;
  else if (abs >= 1000000) body = `${(abs / 1000000).toFixed(1)}M`;
  else if (abs >= 1000) body = `${(abs / 1000).toFixed(1)}K`;
  else body = abs.toLocaleString("en-US");
  if (num > 0) return `+${body}`;
  if (num < 0) return `-${body}`;
  return body;
}

export function formatPnlPercent(pct) {
  const value = Number(pct) || 0;
  if (Math.abs(value) < 0.05) return "0%";
  const abs = Math.abs(value);
  const body = abs >= 10 ? abs.toFixed(0) : abs.toFixed(1);
  if (value > 0) return `+${body}%`;
  if (value < 0) return `-${body}%`;
  return `${body}%`;
}

export function getPnlNeedlePct(pct) {
  const clamped = Math.max(-PNL_MAG_CAP, Math.min(PNL_MAG_CAP, Number(pct) || 0));
  return 50 + (clamped / PNL_MAG_CAP) * 46;
}

export function getPnlCopy(tone, isMine) {
  if (tone === "up") return isMine ? "You are ahead" : "Table ahead";
  if (tone === "down") return isMine ? "Table got hit" : "Table hit";
  return isMine ? "Your table is even" : "Table even";
}
